import { useCallback, useRef, useState } from 'react';
import { trackTermsEvent } from '@/helpers/analytics';
import type { ConsentDocumentKind } from '@/types/consent';

type UseTermsModalOptions = {
  setAcceptedTerms: (value: boolean) => void;
  setAcceptedPrivacy: (value: boolean) => void;
};

export type UseTermsModalResult = {
  openDocument: ConsentDocumentKind | null;
  open: (document: ConsentDocumentKind, triggerElement: HTMLElement) => void;
  close: () => void;
  accept: () => void;
};

function restoreFocus(triggerElement: HTMLElement | null): void {
  if (triggerElement) {
    triggerElement.focus();
  }
}

export function useTermsModal({
  setAcceptedTerms,
  setAcceptedPrivacy,
}: UseTermsModalOptions): UseTermsModalResult {
  const [openDocument, setOpenDocument] = useState<ConsentDocumentKind | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  const open = useCallback((document: ConsentDocumentKind, triggerElement: HTMLElement) => {
    triggerElementRef.current = triggerElement;
    setOpenDocument(document);
    void trackTermsEvent({
      name: 'terms_modal_open',
      params: { document },
    });
  }, []);

  const close = useCallback(() => {
    const triggerElement = triggerElementRef.current;
    setOpenDocument(null);
    queueMicrotask(() => restoreFocus(triggerElement));
  }, []);

  const accept = useCallback(() => {
    if (openDocument === 'terms') {
      setAcceptedTerms(true);
      void trackTermsEvent({
        name: 'terms_modal_accept',
        params: { document: 'terms' },
      });
    } else if (openDocument === 'privacy') {
      setAcceptedPrivacy(true);
      void trackTermsEvent({
        name: 'terms_modal_accept',
        params: { document: 'privacy' },
      });
    }
    close();
  }, [close, openDocument, setAcceptedPrivacy, setAcceptedTerms]);

  return {
    openDocument,
    open,
    close,
    accept,
  };
}
